<template>
  <v-dialog
    v-model="model"
    :persistent="busy"
    max-width="565"
    transition="dialog-bottom-transition"
    content-class="rmm-dialog"
    @keydown.esc="close"
  >
    <v-card class="rmm modal-card">
      <v-btn icon class="close-btn" @click.stop="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>

      <h2 class="modal-title">
        {{ isEdit ? 'Редактировать резерв с маркировкой' : 'Добавить резерв с маркировкой' }}
      </h2>

      <div v-if="isAdmin" class="form-control">
        <div class="field-label">Пользователь</div>

        <v-autocomplete
          v-if="!isUserLocked"
          v-model="form.user"
          :items="userOptions"
          :loading="loading.users"
          item-text="name"
          item-value="id"
          dense
          outlined
          hide-details
          class="field"
          placeholder="Выберите пользователя"
          attach
          clearable
          cache-items
          :menu-props="{ offsetY:true, maxHeight: 320 }"
        />
        <v-text-field
          v-else
          :value="userReadonlyLabel"
          dense
          outlined
          hide-details
          class="field"
          readonly
        />
      </div>

      <div class="form-control">
        <div class="field-label">Название резерва</div>
        <v-text-field
          v-model.trim="form.title"
          dense
          outlined
          hide-details
          class="field"
          placeholder="Например: Резерв на сентябрь"
        />
      </div>

      <div class="form-control">
        <div class="field-label">Маркировка</div>
        <v-select
          v-model="form.markingId"
          :items="markingOptionsFinal"
          :loading="loading.markings"
          item-text="name"
          item-value="id"
          dense
          outlined
          hide-details
          class="field"
          :disabled="lockMarking || !getUser11()"
          placeholder="Выберите маркировку"
          :menu-props="{ offsetY:true, maxHeight: 320 }"
        />
      </div>


      <div v-if="isNewMarking" class="form-control">
        <div class="field-label">Название маркировки</div>
        <v-text-field
          v-model.trim="form.markingName"
          dense
          outlined
          hide-details
          class="field"
          :disabled="lockMarking"
          placeholder="ООО Olimpik: konsultirovanie"
        />
      </div>


      <div class="form-control">
        <div class="field-label">Транк / Количество номеров</div>

        <div
          v-for="(row, idx) in form.trunks"
          :key="row._key"
          class="row-grid"
        >
          <v-select
            v-model="row.queueTrunk"
            :items="trunkOptions"
            :loading="loading.trunks"
            item-text="label"
            item-value="id"
            dense
            outlined
            hide-details
            :class="['field','trunk-select','trunk-wide', { 'is-locked': isPending }]"
            :disabled="lockAmounts"
            :no-data-text="trunkNoDataText"
            placeholder="Транк…"
            :menu-props="{ offsetY:true, maxHeight: 320 }"
            @change="clampRow(idx)"
          />

          <v-text-field
            v-model.number="row.count"
            type="number"
            min="0"
            :max="maxAllowedFor(row.queueTrunk)"
            dense
            outlined
            hide-details
            :class="['field','count-narrow', { 'is-locked': isPending }]"
            :disabled="lockAmounts || !row.queueTrunk"
            :error="!!rowError(idx)"
            :error-messages="rowError(idx)"
            placeholder="0"
            @input="clampRow(idx)"
          >
            <template #append-outer>
              <small v-if="row.queueTrunk" class="hint">
                доступно: {{ maxAllowedFor(row.queueTrunk) }}
              </small>
            </template>
          </v-text-field>

          <v-btn icon class="icon-btn" :disabled="lockAmounts" @click="removeRow(idx)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div v-if="isPending" class="locked-note">
          <v-icon small class="mr-2">mdi-lock</v-icon>
          На стадии «Согласование» редактирование количества номеров недоступно.
        </div>

        <v-btn small text class="add-row" :disabled="lockAmounts" @click="addRow">
          <v-icon small class="mr-2">mdi-plus</v-icon> Добавить транк
        </v-btn>
      </div>

      <div class="form-control">
        <div class="field-label">Количество номеров</div>
        <v-text-field :value="totalCount" dense outlined hide-details class="field" readonly />
      </div>

      <div class="price-card">
        <div class="price-col">
          <div class="price-label">Цена:</div>
          <div class="price-edit">
            <v-text-field
              v-model.number="form.price"
              type="number"
              min="0"
              dense
              outlined
              hide-details
              class="price-field"
              :disabled="lockPrice"
            />
            <span class="price-suf">₽ / номер</span>
          </div>
        </div>
        <div class="price-col right">
          <div class="price-label">Стоимость операции</div>
          <div class="price-value">{{ total | rub }}</div>
        </div>
      </div>

      <div class="form-control">
        <v-checkbox
          v-model="form.autorenew"
          hide-details
          class="checkbox"
          color="#4B5EFF"
          label="Автопродление аренды"
        />
        <!-- <div v-if="lockAutorenew" class="hint muted">Недоступно для изменения в текущем статусе.</div> -->
      </div>

      <div v-if="apiError" class="money-error">{{ apiError }}</div>

      <v-btn
        block
        height="48"
        class="primary-btn mt-4"
        :disabled="!isValid || busy"
        :loading="busy"
        @click="submit"
      >
        {{ primaryCta }}
      </v-btn>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'ReserveMarkingModal',
  props: {
    value:          { type:Boolean, required:true },
    pricePerNumber: { type:Number, default:0 },
    isAdmin:        { type:Boolean, default:false },
    mode:           { type:String,  default:'create' },
    status:         { type:String,  default:'review' },
    users:          { type:Array,   default:() => [] },
    markings:       { type:Array,   default:() => [] },
    reserve:        { type:Object,  default:() => ({}) },
    allUsersUrl:    { type:String,  default:'/numbers/users_all' }
  },
  emits: ['input','create','save'],
  data(){
    return {
      busy:false,
      apiError:'',
      loading:{ users:false, markings:false, trunks:false },
      allUsers:[],
      markingsLocal:[],
      trunkPools:{},
      NEW_ID:'__new__',
      keySeq:1,
      initialAutorenew:false,
      form:{
        user:null,
        title:'',
        markingId:null,
        markingName:'',
        trunks:[{ _key:1, queueTrunk:null, count:0 }],
        price: 0,
        autorenew:true
      }
    }
  },
  computed:{
    model:{ get(){ return this.value }, set(v){ this.$emit('input', v) } },
    isEdit(){ return this.mode==='edit' },

    isPending(){ return String(this.status)==='pending' },

    isUserLocked(){ return this.isEdit ? true : (this.isAdmin && this.status!=='review') },
    lockAmounts(){ return this.isEdit && (this.status==='pending' || this.status==='active') },
    lockMarking(){ return this.isEdit && this.status==='active' },
    lockPrice(){ return this.isEdit && this.status!=='review' },
    lockAutorenew(){ return String(this.status) !== 'active' },

    primaryCta(){ return this.isEdit ? 'Сохранить' : 'Добавить' },

    userOptions(){
      const base = (this.users||[])
        .filter(u => u && (u.id || u.name) && u.id!=='__all__')
        .map(u => ({ id:String(u.id||u.name||''), name: u.name || u.id }))
        .filter(u => u.id)
      return this.allUsers.length ? this.allUsers : base
    },
    userReadonlyLabel(){
      const id = String(this.form.user||'')
      const src = this.allUsers.length ? this.allUsers : (this.users||[])
      const f = src.find(u => String(u.id||u.name)===id)
      return f ? (f.name||f.id) : id
    },

    markingOptionsFinal(){
      const base = this.markingsLocal.length ? this.markingsLocal : (this.markings||[])
      const norm = base.map(x => ({ id: String(x.id), name: x.name }))
      return [{ id:this.NEW_ID, name:'Новая маркировка' }, ...norm]
    },
    isNewMarking(){ return this.form.markingId===this.NEW_ID },

    trunkOptions(){
      const pools = this.trunkPools || {}
      return Object.keys(pools)
        .sort()
        .map(id => {
          const p = pools[id] || {}
          return { id: String(id), label: String(id), rp: Number(p.reserve_pool ?? 0) }
        })
        .filter(x => x.id)
    },
    trunkNoDataText(){ return this.loading.trunks ? 'Загрузка…' : 'Нет транков' },
    rpMap(){
      const map = {}
      for (const t of this.trunkOptions) map[t.id] = Number(t.rp||0)
      return map
    },

    totalCount(){ return (this.form.trunks||[]).reduce((s,r)=>s+(Number(r.count)||0),0) },
    total(){ return (Number(this.form.price)||0) * this.totalCount },

    isValid(){
      const status = String(this.status||'review')
      if (!this.isEdit && this.isAdmin && !this.form.user) return false
      if (!(this.form.title||'').trim()) return false

      if (this.isEdit && status==='active') return true
      if (this.isEdit && status==='pending') {
        if (!this.form.markingId) return false
        if (this.isNewMarking && !(this.form.markingName||'').trim()) return false
        return true
      }

      if (!this.form.markingId) return false
      if (this.isNewMarking && !(this.form.markingName||'').trim()) return false
      if (!this.form.trunks.length) return false
      for (const r of this.form.trunks) {
        if (!r.queueTrunk) return false
        const c = Number(r.count)||0
        if (c<=0) return false
        if (c > this.maxAllowedFor(r.queueTrunk)) return false
      }
      if (!Number.isFinite(Number(this.form.price)) || Number(this.form.price)<0) return false
      return true
    }
  },
  watch:{
    value:{
      immediate:true,
      async handler(v){
        if (!v) return
        this.apiError=''
        await this.loadTrunkPools()
        this.prefill()

        if (this.isAdmin && !this.isUserLocked && !this.allUsers.length) {
          await this.loadAllUsers()
        }

        const u = this.getUser11()
        if (u) {
          await Promise.all([ this.loadMarkingsForUser(u), this.loadPpn(u) ])
        }
      }
    },
    'form.user':{
      async handler(){
        if (this.isEdit) return
        const u = this.getUser11()
        this.form.markingId = null
        this.form.markingName = ''
        if (!u) { this.markingsLocal=[]; this.form.price=0; return }
        await Promise.all([ this.loadMarkingsForUser(u), this.loadPpn(u) ])
      }
    }
  },
  methods:{
    /* ===== utils ===== */
    close(){ if(!this.busy) this.model=false },
    getUser11(){
      const raw = this.isAdmin ? (this.form.user || '') : (this.$root?.user?.login || '')
      const id = String(raw).replace(/\D/g,'')
      return id.length===11 ? id : ''
    },
    pick(obj, pathArr, def){
      try{
        let cur = obj
        for (const k of pathArr) {
          if (cur == null) return def
          cur = cur[k]
        }
        return cur == null ? def : cur
      } catch { return def }
    },

    /* ===== init/prefill ===== */
    prefill(){
      this.keySeq = 1
      if (!this.isEdit) {
        this.form = {
          user:null, title:'',
          markingId:null, markingName:'',
          trunks:[{ _key:1, queueTrunk:null, count:0 }],
          price: 0,
          autorenew:true
        }
        this.initialAutorenew = true
        return
      }
      const r = this.reserve || {}
      this.form.title = r.title || r.reserve || this.form.title
      this.form.user = r.user || this.form.user
      if (r.markingId) { this.form.markingId = String(r.markingId); this.form.markingName='' }
      else { this.form.markingId = this.NEW_ID; this.form.markingName = r.markingName || r.marking || '' }

      const src = Array.isArray(r.trunks)&&r.trunks.length ? r.trunks
                : Array.isArray(r.plan) ? r.plan : []
      this.form.trunks = (src.length ? src : [{queueTrunk:null,count:0}]).map(x => ({
        _key: ++this.keySeq,
        queueTrunk: String(x.queueTrunk || x.carouselId || x.carousel || ''),
        count: Number(x.count || x.numbers || 0) || 0
      }))
      this.form.price = Number(r.price)||0


      const ar = (typeof r.autorenew === 'boolean') ? r.autorenew
               : (String(r.autoRenew||'').toLowerCase()==='включено')
      this.form.autorenew = !!ar
      this.initialAutorenew = !!ar
    },

    reconcileMarkingByName(){
      if (this.form.markingId || !this.form.markingName) return
      const name = (this.form.markingName || '').trim().toLowerCase()
      if (!name) return
      const hit = (this.markingsLocal || []).find(m => (m.name || '').trim().toLowerCase() === name)
      if (hit && hit.id) {
        this.form.markingId = String(hit.id)
        this.form.markingName = ''
      }
    },

    /* ===== loaders ===== */
    async loadAllUsers(){
      this.loading.users = true
      try{
        const resp = await new Promise(resolve =>
          this.$http.post(this.allUsersUrl, {}, r => resolve(r))
        )
        const users = this.pick(resp, ['users'], [])
        const arr = Array.isArray(users) ? users : []
        this.allUsers = arr.map(u => {
          const id = String(u.id || u.name || '').replace(/\D/g,'')
          const name = u.name || id
          return id ? { id, name } : null
        }).filter(Boolean)
      } finally { this.loading.users = false }
    },

    async loadTrunkPools () {
      this.loading.trunks = true
      try {
        const resp = await new Promise(resolve =>
          this.$http.post('/numbers/trunk_pool_lengths', {}, r => resolve(r))
        )
        this.trunkPools = this.normalizeTrunkPools(resp)
      } finally {
        this.loading.trunks = false
      }
    },
    normalizeTrunkPools(raw){
      if (!raw) return {}
      if (typeof raw === 'string') { try { raw = JSON.parse(raw) } catch { return {} } }
      if (raw && typeof raw === 'object' && raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)) {
        raw = raw.data
      }
      if (raw && typeof raw === 'object' && raw.ok === false) return {}
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw
      return {}
    },

    async loadMarkingsForUser(user11){
      this.loading.markings = true
      try{
        const resp = await new Promise(resolve =>
          this.$http.post('/numbers/markings/list', { user:user11 }, r => resolve(r))
        )
        const arr = this.pick(resp, ['markings'], [])
        this.markingsLocal = (Array.isArray(arr) ? arr : []).map(x => ({ id:String(x.id), name:x.name }))
        this.reconcileMarkingByName()
      } finally { this.loading.markings = false }
    },

    async loadPpn(user11){
      try{
        const resp = await new Promise(resolve =>
          this.$http.post('/numbers/price_per_number', { user:user11 }, r => resolve(r))
        )
        const price = Number(this.pick(resp, ['price'], 0))
        if (Number.isFinite(price) && price>=0) this.form.price = price
      } catch { /* noop */ }
    },

    /* ===== rows ===== */
    addRow(){ if(this.lockAmounts) return; this.form.trunks.push({ _key: ++this.keySeq, queueTrunk:null, count:0 }) },
    removeRow(i){ if(this.lockAmounts) return; this.form.trunks.splice(i,1); if(!this.form.trunks.length) this.addRow() },

    maxAllowedFor(q){ if (!q) return 0; return Number(this.rpMap[String(q)] || 0) },

    clampRow(idx){
      const r = this.form.trunks[idx]
      if (!r) return
      const max = this.maxAllowedFor(r.queueTrunk)
      const val = Number(r.count) || 0
      const clamped = Math.min(max, Math.max(0, val))
      if (clamped !== r.count) {
        this.$set(this.form.trunks, idx, { ...r, count: clamped })
      }
    },

    rowError(idx){
      const r = this.form.trunks[idx]
      if (!r?.queueTrunk) return ''
      const c = Number(r.count)||0
      const max = this.maxAllowedFor(r.queueTrunk)
      if (c>max) return `Доступно в reserve_pool: ${max}`
      return ''
    },

    /* ===== submit ===== */
async submit(){
  if (!this.isValid) return
  const user = this.isAdmin ? this.getUser11() : undefined

  const canTouchAutorenew = this.isEdit && String(this.status) === 'active'

  const payload = {
    user,
    markingId: this.isNewMarking ? null : this.form.markingId,
    markingName: this.isNewMarking ? (this.form.markingName||'').trim() : null,
    trunks: (this.form.trunks||[]).map(r => ({ queueTrunk:String(r.queueTrunk), count: Math.max(0, Number(r.count)||0) })),
    price: Math.max(0, Number(this.form.price)||0),
    title: (this.form.title||'').trim(),
    autorenew: (!this.isEdit || canTouchAutorenew) ? !!this.form.autorenew : false
  }

  this.busy = true; this.apiError=''
  try{
    const url = this.isEdit ? '/numbers/reserves/update' : '/numbers/reserves/create'
    const body = this.isEdit
      ? {
          id: this.reserve?.id,
          markingId: payload.markingId,
          markingName: payload.markingName,
          price: payload.price,
          plan: payload.trunks,
          title: payload.title
        }
      : payload

    const resp = await new Promise(resolve => this.$http.post(url, body, r => resolve(r)))

    if (resp && resp.ok) {
      if (canTouchAutorenew && this.form.autorenew !== this.initialAutorenew && this.reserve?.id) {
        try {
          await new Promise(resolve =>
            this.$http.post('/numbers/reserves/update_user',
              { id: this.reserve.id, autorenew: !!this.form.autorenew },
              r => resolve(r)
            )
          )
        } catch(_) {console.log()}
      }

      const totalCount = (payload.trunks||[]).reduce((s,x)=>s+Number(x.count||0),0)
      const out = { ...payload, totalCount, total: (Number(payload.price)||0) * totalCount }
      this.$emit(this.isEdit ? 'save' : 'create', out)
      this.busy = false
      this.close()
      return
    } else {
      this.apiError = (resp && (resp.message || resp.error)) || 'Не удалось сохранить'
    }
  } catch {
    this.apiError = 'Сбой сети/сервера'
  } finally { this.busy=false }
}

  },
  filters:{
    rub(v){
      const n=Number(v)||0
      try{ return new Intl.NumberFormat('ru-RU').format(n)+' ₽' }
      catch{ return n+' ₽' }
    }
  }
}
</script>

<style scoped>
/* палитра и контейнер */
.rmm{
  --c-text:#2B2D33; --c-muted:#808185; --c-line:#D9DADE;
  --c-bg:#F8F8F8; --c-accent:#4B5EFF; --c-warn:#F0AD4E; --c-warn-bg:#FFF7E6;
}
.rmm.modal-card{
  position:relative;
  padding:32px 32px 28px;
  border-radius:12px;
  box-shadow:0 18px 50px rgba(0,0,0,.14);
  background:#fff;
}

/* заголовок и крестик */
.modal-title{
  margin:0 0 20px;
  font:800 24px/1.2 Manrope,system-ui,sans-serif;
  text-align:center;
  color:var(--c-text);
}
.close-btn{
  position:absolute; top:12px; right:12px;
  min-width:36px; height:36px;
}
.close-btn .v-icon{ font-size:20px; color:#9C9DA1; }

/* поля */
.form-control{ margin-top:16px; }
.field-label{
  font:600 15px/1.2 Manrope,sans-serif;
  color:#404247;
  margin-bottom:6px;
  text-align:left;
}

/* общая высота для всех инпутов/селектов */
.field .v-input__slot{
  min-height:44px;
  height:44px;
  border-radius:10px;
  border-color:var(--c-line)!important;
}
.field .v-input__slot:hover,
.field .v-input--is-focused .v-input__slot{
  border-color:var(--c-accent)!important;
}
.field input,
.field .v-select__selections{
  font:500 14px/1.2 Manrope,sans-serif;
  color:var(--c-text);
}

/* транки */
.row-grid{ display:flex; gap:8px; align-items:center; margin-bottom:8px; }
.trunk-select .v-select__selections{ white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

/* широкий селект и узкое поле */
.trunk-wide{ flex:1; min-width:260px; }
.count-narrow{ width:120px; }

/* подсветка «заблокировано» (стадия согласование) */
.is-locked .v-input__slot{
  background: var(--c-warn-bg);
  border-color: var(--c-warn) !important;
}
.locked-note{
  margin: 4px 0 6px;
  padding: 8px 10px;
  background: var(--c-warn-bg);
  border: 1px dashed var(--c-warn);
  color: #7a5a18;
  border-radius: 8px;
  display:flex; align-items:center;
  font:600 13px/1.2 Manrope, sans-serif;
}

.icon-btn{ min-width:44px; height:44px; }
.add-row{ margin-top:4px; }

.price-card{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:12px;
  background:#fff;
  border:1px solid var(--c-line);
  border-radius:12px;
  padding:14px 16px;
  margin-top:16px;
}
.price-col.right{ text-align:right; }
.price-label{ font:600 12px/1.2 Manrope,sans-serif; color:#7B7D83; margin-bottom:2px; }
.price-value{ font:800 20px/1.25 Manrope,sans-serif; color:var(--c-text); }
.price-edit{ display:flex; align-items:center; gap:8px; }
.price-field .v-input__slot{ min-height:40px; height:40px; border-radius:10px; width:120px; }
.price-suf{ font:800 16px/1.2 Manrope,sans-serif; color:var(--c-text); }

.hint{ opacity:.85; font-size:12px; }
.hint.muted{ color:#7B7D83; margin-top:4px; }
.money-error{ margin-top:10px; font:600 14px/1.25 Manrope,sans-serif; color:#EE505A; }
.primary-btn{
  background:#4B5EFF!important;
  color:#fff!important;
  font:800 16px/1.1 Manrope,sans-serif;
  border-radius:12px!important;
  text-transform:none; letter-spacing:0;
}
.primary-btn.v-btn--disabled{ opacity:.6!important; }
</style>
